// src/stores/usePageLoader.ts - version 0.1
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

interface LoaderState {
  isLoading: boolean
  loadingMessage: string
  progress: number
  showFullLoader: boolean
  requestCount: number
}

export const usePageLoaderStore = defineStore('pageLoader', () => {
  // State
  const isLoading = ref(false)
  const loadingMessage = ref('Loading...')
  const progress = ref(0)
  const showFullLoader = ref(false)
  const requestCount = ref(0)
  
  // Timers
  let loaderTimer: number | null = null
  let progressTimer: number | null = null
  
  // Getters
  const hasActiveRequests = computed(() => requestCount.value > 0)
  const loadingPercentage = computed(() => Math.round(progress.value * 100))
  
  // Actions
  function setLoading(loading: boolean) {
    if (loading) {
      requestCount.value++
      isLoading.value = true
    } else {
      requestCount.value = Math.max(0, requestCount.value - 1)
      if (requestCount.value === 0) {
        isLoading.value = false
        progress.value = 0
      }
    }
  }
  
  function showLoader(message = 'Loading your experience...') {
    showFullLoader.value = true
    loadingMessage.value = message
    progress.value = 0
    
    // Simulate progress
    simulateProgress()
  }
  
  function hideLoader() {
    // Complete progress animation
    progress.value = 1
    
    setTimeout(() => {
      showFullLoader.value = false
      progress.value = 0
      clearTimers()
    }, 300)
  }
  
  function updateProgress(value: number) {
    progress.value = Math.min(1, Math.max(0, value))
  }
  
  function updateMessage(message: string) {
    loadingMessage.value = message
  }
  
  function simulateProgress() {
    let currentProgress = 0
    const increment = 0.01
    
    progressTimer = setInterval(() => {
      if (currentProgress < 0.9) {
        currentProgress += increment * (1 - currentProgress) // Slow down as it progresses
        updateProgress(currentProgress)
      } else {
        if (progressTimer) clearInterval(progressTimer)
      }
    }, 100)
  }
  
  function clearTimers() {
    if (loaderTimer) {
      clearTimeout(loaderTimer)
      loaderTimer = null
    }
    if (progressTimer) {
      clearInterval(progressTimer)
      progressTimer = null
    }
  }
  
  function reset() {
    isLoading.value = false
    loadingMessage.value = 'Loading...'
    progress.value = 0
    showFullLoader.value = false
    requestCount.value = 0
    clearTimers()
  }
  
  return {
    // State
    isLoading,
    loadingMessage,
    progress,
    showFullLoader,
    requestCount,
    
    // Getters
    hasActiveRequests,
    loadingPercentage,
    
    // Actions
    setLoading,
    showLoader,
    hideLoader,
    updateProgress,
    updateMessage,
    reset
  }
})